<script setup lang="ts">import { toRef as _toRef } from 'vue';

import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import DashboardSearchItem from './DashboardSearchItem.vue'
import {watchDebounced} from '@vueuse/core'
import {IGlobalSearchResultItem} from '@toolify/server/src/controllers/GlobalSearchController/types/IGlobalSearchResultItem'
import {IResourceItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceItemResponseInstance'
import {useResourceItemStore} from '@toolify/client/src/stores/ResourceItemStore/useResourceItemStore'
import {useSpaceStore} from '@toolify/client/src/stores/SpaceStore/useSpaceStore'

import {useI18n} from 'vue-i18n'
import {useGlobalSearchApiStore} from '@toolify/client/src/stores/api/GlobalSearchApiStore/useGlobalSearchApiStore'
import LoadingComponent from '@toolify/client/src/modules/ui/components/LoadingComponent.vue'
import SearchInputComponent from '@toolify/client/src/modules/ui/components/SearchInputComponent.vue'
import SearchFilterListComponent from '@toolify/client/src/modules/ui/components/SearchFilterListComponent.vue'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {useRouter} from 'vue-router'
import {ResourceTypeDataMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeDataMap'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {CloudItemType} from '@toolify/server/src/models/mongoose/CloudItemModel/enum/CloudItemType'
import {ICloudItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ICloudItemResponseInstance'
import {IGlobalSearchDatabaseViewPayload, IGlobalSearchDatabaseItemFileInterfaces} from '@toolify/server/src/controllers/GlobalSearchController/types/IGlobalSearchInterfacesByTypeMap'
import {useAppLayoutStore} from '@toolify/client/src/stores/AppLayoutStore/useAppLayoutStore'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import type {RecentDatabaseItemEntry} from '@toolify/client/src/stores/RecentDatabaseItemsStore/useRecentDatabaseItemsStore'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {IFileResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileResponseInstance'
import {FileThumbnailDisplayType} from '@toolify/client/src/modules/files/enum/FileThumbnailDisplayType'
import {FileThumbnailType} from '@toolify/client/src/modules/files/enum/FileThumbnailType'
import {CommonFileTypeToFileThumbnailDisplayTypeMap} from '@toolify/client/src/modules/files/maps/CommonFileTypeToFileThumbnailDisplayTypeMap'
import {FileThumbnailDisplayTypeToCommonThumbnailDataMap} from '@toolify/client/src/modules/files/maps/FileThumbnailDisplayTypeToCommonThumbnailDataMap'
import {getBestImageFileVariant} from '@toolify/client/src/modules/ui/functions/getBestImageFileVariant'

const {t} = useI18n()
const appLayoutStore = useAppLayoutStore()
const workspaceStore = useWorkspaceStore()
const __$temp_1 = (useResourceItemStore()),
  resourceItems = _toRef(__$temp_1, 'resourceItems'),
  sortedResourceItems = _toRef(__$temp_1, 'sortedResourceItems');
const __$temp_2 = (useSpaceStore()),
  currentSpace = _toRef(__$temp_2, 'currentSpace');
const searchApiStore = useGlobalSearchApiStore()
const router = useRouter()

useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Escape], () => {
    appLayoutStore.closeSearchOverlay()
  }],
], () => appLayoutStore.isSearchOverlayOpen)

let unsubscribeAfterEach: (() => void) | undefined
onMounted(() => {
  unsubscribeAfterEach = router.afterEach((to, from, failure) => {
    if (failure) {
      return
    }
    if (to.fullPath === from.fullPath) {
      return
    }
    if (!appLayoutStore.isSearchOverlayOpen) {
      return
    }
    appLayoutStore.closeSearchOverlay()
  })
})
onUnmounted(() => {
  unsubscribeAfterEach?.()
})

const searchInputRef = ref<{focus:() => void} | null>(null)
const homeSearchRootRef = ref<HTMLElement | null>(null)

watch(() => appLayoutStore.searchInputFocusNonce, () => {
  void nextTick(() => {
    searchInputRef.value?.focus()
  })
})

type IGlobalSearchDatabaseItemFilePayload = IGlobalSearchDatabaseItemFileInterfaces['payload']

type SearchResultItem = {
  key: string
  label: string
  description: string
  typeLabel: string
  type: ResourceType
  item: IResourceItemResponseInstance | IGlobalSearchResultItem | null
  isGlobalResult: boolean
  snippetParts?: {text: string, isHit: boolean}[]
  leadingImageUrl?: string
  leadingIconName?: IconNameType
  descriptionIconName?: IconNameType
  isDatabaseItem?: boolean
  databaseId?: string
  databaseItemId?: string
}

/**
 * One page of server results; the dropdown appends the next page when the user scrolls to its end.
 * The end is detected by a short page — the endpoint reports no total, and the strategies' own caps
 * bound how far the walk can go anyway.
 */
const GLOBAL_SEARCH_PAGE_SIZE = 30

const searchText = ref('')
const submittedSearchText = ref('')
const suggestionServerResults = ref<IGlobalSearchResultItem<any>[]>([])
const submittedServerResults = ref<IGlobalSearchResultItem<any>[]>([])
const isLoadingGlobalResults = ref(false)
const hasMoreServerResults = ref(false)
const isLoadingMoreServerResults = ref(false)
const isLoadingSubmittedResults = ref(false)
const currentSuggestionIndex = ref(-1)
const isFullSearchActive = ref(false)
const activeTypeFilter = ref<string>('all')

function getTypeFilterIcon(id: string) {
  if (id === 'all') {
    return IconNameType.LayerGroup
  }
  switch (id as ResourceType) {
      case ResourceType.Database:
        return IconNameType.Database
      case ResourceType.DatabaseView:
        return IconNameType.Table
      case ResourceType.DatabaseItem:
        return IconNameType.List
      case ResourceType.Folder:
        return IconNameType.Files
      case ResourceType.CloudItem:
      case ResourceType.DatabaseItemFile:
        return IconNameType.File
      case ResourceType.Conversation:
      case ResourceType.DirectConversation:
        return IconNameType.CommentLines
      default:
        return IconNameType.LayerGroup
  }
}

watch(searchText, (value) => {
  /** A changed query invalidates the loaded pages — stop the load-more sentinel until the new first page lands. */
  hasMoreServerResults.value = false
  if (value) {
    isLoadingGlobalResults.value = true
    return
  }
  isLoadingGlobalResults.value = false
  suggestionServerResults.value = []
  submittedServerResults.value = []
  submittedSearchText.value = ''
  currentSuggestionIndex.value = -1
  isFullSearchActive.value = false
  activeTypeFilter.value = 'all'
})

watchDebounced(searchText, async (value) => {
  if (!value) {
    suggestionServerResults.value = []
    isLoadingGlobalResults.value = false
    return
  }
  try {
    const {results} = await searchApiStore.search(value, GLOBAL_SEARCH_PAGE_SIZE)
    if (value !== searchText.value) {
      return
    }
    suggestionServerResults.value = results
    hasMoreServerResults.value = results.length === GLOBAL_SEARCH_PAGE_SIZE
  } catch (e) {
    suggestionServerResults.value = []
  } finally {
    isLoadingGlobalResults.value = false
  }
}, {debounce: 300})

async function onLoadMoreSuggestions() {
  const query = searchText.value
  if (!query || isLoadingGlobalResults.value || isLoadingMoreServerResults.value || !hasMoreServerResults.value) {
    return
  }
  isLoadingMoreServerResults.value = true
  try {
    /**
     * The offset is how many raw results are already loaded — display-level dedupe must not shift it,
     * or pages would overlap. A response for an already-changed query is dropped, not appended.
     */
    const {results} = await searchApiStore.search(query, GLOBAL_SEARCH_PAGE_SIZE, suggestionServerResults.value.length)
    if (query !== searchText.value) {
      return
    }
    suggestionServerResults.value = [...suggestionServerResults.value, ...results]
    hasMoreServerResults.value = results.length === GLOBAL_SEARCH_PAGE_SIZE
  } catch (e) {
    hasMoreServerResults.value = false
  } finally {
    isLoadingMoreServerResults.value = false
  }
}

const localSuggestionItems = computed(() => {
  if (!searchText.value) {
    return []
  }
  const lowerSearch = searchText.value.toLowerCase()
  return (resourceItems.value || []).filter(item => {
    return item.name?.toLowerCase().includes(lowerSearch)
  })
})

const recentResourceItemsForIdleSuggestions = computed(() => {
  const sorted = sortedResourceItems.value
  if (!sorted?.length) {
    return []
  }
  const favouriteIds = new Set(
    sorted.filter(item => item.isFavouriteToRequestingEntity).map(item => item.id),
  )
  return sorted
    .filter(resourceItem => {
      if (favouriteIds.has(resourceItem.id)) {
        return false
      }
      if (!resourceItem.readAt) {
        return false
      }
      if (currentSpace.value?.id && resourceItem.resourceSpaceId !== currentSpace.value.id) {
        return false
      }
      return true
    })
    .slice()
    .sort((a, b) => {
      const aReadAt = new Date(a.readAt as unknown as string).getTime() || 0
      const bReadAt = new Date(b.readAt as unknown as string).getTime() || 0
      return bReadAt - aReadAt
    })
    .slice(0, 10)
})

const localSubmittedItems = computed(() => {
  if (!submittedSearchText.value) {
    return []
  }
  const lowerSearch = submittedSearchText.value.toLowerCase()
  return (resourceItems.value || []).filter(item => {
    return item.name?.toLowerCase().includes(lowerSearch)
  })
})

function getSearchResultTypeLabel(type: ResourceType) {
  if (type === ResourceType.DatabaseItem) {
    return t('general.databaseItem')
  }
  if (type === ResourceType.DatabaseView) {
    return t('general.databaseView')
  }
  if (type === ResourceType.Folder) {
    return t('general.folder')
  }
  if (type === ResourceType.CloudItem) {
    return t('general.file')
  }
  if (type === ResourceType.DatabaseItemFile) {
    return t('general.databaseItemFile')
  }
  return type
}

/**
 * The same visual the full results list leads with, shrunk to dropdown size: a real thumbnail when the
 * file has image variants, the file-type icon otherwise — file rows are recognisable at a glance again.
 */
function getFileLeadingVisual(file: IFileResponseInstance): {imageUrl?: string, iconName: IconNameType} {
  const displayType = CommonFileTypeToFileThumbnailDisplayTypeMap.get(file.commonFileType) || FileThumbnailDisplayType.Unknown
  const commonData = FileThumbnailDisplayTypeToCommonThumbnailDataMap.get(displayType)?.(file)
  const imageUrl = commonData?.thumbnailType === FileThumbnailType.Image
    ? getBestImageFileVariant(file.variants, 36, 36, window.devicePixelRatio || 1)?.url ?? undefined
    : undefined
  return {
    imageUrl,
    iconName: commonData?.iconNameType ?? IconNameType.File,
  }
}

function getLocalSearchResult(item: IResourceItemResponseInstance): SearchResultItem {
  const resourceTypeData = item.resourceEntity ? ResourceTypeDataMap.get(item.resourceEntity.resourceType) : null
  const resourceData = resourceTypeData ? resourceTypeData(item.resourceEntity) : null
  return {
    key: `local-${item.id}`,
    label: item.name || '',
    description: '',
    typeLabel: getSearchResultTypeLabel(item.resourceEntity.resourceType),
    type: item.resourceEntity.resourceType,
    item,
    isGlobalResult: false,
    leadingIconName: item.iconNameType || resourceData?.iconClass || IconNameType.File,
  }
}

function getRecentDatabaseItemSearchResult(entry: RecentDatabaseItemEntry): SearchResultItem {
  return {
    key: `db-item-${entry.databaseId}-${entry.databaseItemId}`,
    label: entry.title || t('general.databaseItem'),
    description: '',
    typeLabel: t('general.databaseItem'),
    type: ResourceType.DatabaseItem,
    item: null,
    isGlobalResult: false,
    isDatabaseItem: true,
    databaseId: entry.databaseId,
    databaseItemId: entry.databaseItemId,
    leadingIconName: IconNameType.Table,
  }
}

function getGlobalSearchResult(item: IGlobalSearchResultItem): SearchResultItem {
  const payload = item.typePayload as IGlobalSearchDatabaseViewPayload | ICloudItemResponseInstance
  /**
   * An attachment's title is genuinely blankable (rename accepts ''), and falling through to the
   * resource item would label the file with its database's name — the underlying file's own name is
   * the honest fallback.
   */
  const label = payload?.name
    || (payload as unknown as {title?: string})?.title
    || (payload as unknown as {file?: {originalName?: string}})?.file?.originalName
    || item.resourceItem?.name
    || ''

  let leadingImageUrl: string | undefined
  let leadingIconName: IconNameType
  if(item.type === ResourceType.CloudItem) {
    const cloudItem = payload as ICloudItemResponseInstance
    if(cloudItem?.type === CloudItemType.Catalog) {
      leadingIconName = IconNameType.Folder
    } else if(cloudItem?.file) {
      const visual = getFileLeadingVisual(cloudItem.file)
      leadingImageUrl = visual.imageUrl
      leadingIconName = visual.iconName
    } else {
      leadingIconName = IconNameType.File
    }
  } else if(item.type === ResourceType.DatabaseItemFile) {
    const databaseItemFile = payload as unknown as IGlobalSearchDatabaseItemFilePayload
    if(databaseItemFile?.file) {
      const visual = getFileLeadingVisual(databaseItemFile.file)
      leadingImageUrl = visual.imageUrl
      leadingIconName = visual.iconName
    } else {
      leadingIconName = IconNameType.File
    }
  } else {
    leadingIconName = (payload as {iconNameType?: IconNameType})?.iconNameType || IconNameType.Table
  }

  /** The server spells out where the result lives (folder/catalog chain, database/item); the origin resource name is the fallback. */
  const locationPathNames = (payload as unknown as {locationPathNames?: string[] | null})?.locationPathNames
  /** A deep chain reads as noise — the ends carry the meaning, so the middle collapses: `root / … / parent`. */
  const formatLocationPath = (parts: string[]) => (parts.length > 3
    ? `${parts[0]} / … / ${parts[parts.length - 1]}`
    : parts.join(' / '))
  return {
    key: `global-${item.type}-${payload?.id || item.resourceItem?.id}`,
    label,
    description: locationPathNames?.length ? formatLocationPath(locationPathNames) : item.resourceItem?.name || '',
    typeLabel: getSearchResultTypeLabel(item.type),
    type: item.type,
    item,
    isGlobalResult: true,
    snippetParts: (payload as {contentMatchSnippetParts?: {text: string, isHit: boolean}[]})?.contentMatchSnippetParts ?? undefined,
    leadingImageUrl,
    leadingIconName,
    descriptionIconName: item.resourceItem?.iconNameType
      || (item.resourceItem?.resourceEntity
        ? ResourceTypeDataMap.get(item.resourceItem.resourceEntity.resourceType)?.(item.resourceItem.resourceEntity)?.iconClass
        : undefined)
      || undefined,
  }
}

const idleMergedSuggestionItems = computed(() => {
  const wsId = workspaceStore.currentWorkspace?.id
  if (!wsId) {
    return recentResourceItemsForIdleSuggestions.value.map(getLocalSearchResult).slice(0, 8)
  }
  type IdleRow = {sortAt: number; item: SearchResultItem}
  const rows: IdleRow[] = []
  for (const r of recentResourceItemsForIdleSuggestions.value) {
    rows.push({
      sortAt: new Date(r.readAt as unknown as string).getTime() || 0,
      item: getLocalSearchResult(r),
    })
  }
  return rows
    .sort((a, b) => b.sortAt - a.sortAt)
    .slice(0, 8)
    .map((row) => row.item)
})


const suggestionItems = computed(() => {
  if (!searchText.value.trim()) {
    return idleMergedSuggestionItems.value
  }
  const items = [
    ...localSuggestionItems.value.map(getLocalSearchResult),
    ...suggestionServerResults.value.map(getGlobalSearchResult),
  ]
  /**
   * Deduplicated but NOT capped: the list owns its scrolling and pages in more results on demand —
   * an arbitrary cut here silently hid everything past the first handful of matches.
   */
  return items.filter((item, index, currentItems) => {
    return currentItems.findIndex((currentItem) => {
      return currentItem.key === item.key
    }) === index
  })
})

const hasResults = computed(() => {
  return !!localSubmittedItems.value.length || !!submittedServerResults.value.length
})

const fullSearchResults = computed<SearchResultItem[]>(() => {
  return [
    ...localSubmittedItems.value.map(getLocalSearchResult),
    ...submittedServerResults.value.map(getGlobalSearchResult),
  ]
})

const availableTypeFilters = computed(() => {
  const uniqueTypes = fullSearchResults.value.reduce((acc, item) => {
    if (acc.includes(item.type)) {
      return acc
    }
    acc.push(item.type)
    return acc
  }, [] as ResourceType[])

  return [
    {
      id: 'all',
      label: t('general.all'),
      count: fullSearchResults.value.length,
      iconNameType: IconNameType.LayerGroup,
    },
    ...uniqueTypes.map(type => {
      return {
        id: type,
        label: getSearchResultTypeLabel(type),
        count: fullSearchResults.value.filter(item => item.type === type).length,
        iconNameType: getTypeFilterIcon(type),
      }
    }),
  ]
})

watch(availableTypeFilters, (filters) => {
  if (filters.some(filterItem => filterItem.id === activeTypeFilter.value)) {
    return
  }
  activeTypeFilter.value = 'all'
})

const filteredFullSearchResults = computed(() => {
  if (activeTypeFilter.value === 'all') {
    return fullSearchResults.value
  }
  return fullSearchResults.value.filter(item => item.type === activeTypeFilter.value)
})

async function navigateToSearchResult(searchResult: SearchResultItem) {
  if (searchResult.isDatabaseItem && searchResult.databaseId && searchResult.databaseItemId) {
    const workspaceName = workspaceStore.currentWorkspace?.name
    if (!workspaceName) {
      return
    }
    await router.push({
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: searchResult.databaseId,
        databaseItemId: searchResult.databaseItemId,
      },
    })
    return
  }
  if (!searchResult.isGlobalResult) {
    const resourceItem = searchResult.item as IResourceItemResponseInstance
    const resourceTypeData = ResourceTypeDataMap.get(resourceItem.resourceEntity.resourceType)
    const data = resourceTypeData ? resourceTypeData(resourceItem.resourceEntity) : null
    if (data?.link) {
      await router.push(data.link)
    }
    return
  }

  const globalResult = searchResult.item as IGlobalSearchResultItem
  if (globalResult.type === ResourceType.DatabaseView) {
    const databaseViewPayload = globalResult.typePayload as IGlobalSearchDatabaseViewPayload
    await router.push({
      name: RouteName.DatabaseView,
      params: {
        databaseId: databaseViewPayload.databaseId,
        databaseViewId: databaseViewPayload.id,
      },
    })
    return
  }

  if (globalResult.type === ResourceType.DatabaseItemFile) {
    const databaseItemFile = globalResult.typePayload as IGlobalSearchDatabaseItemFilePayload
    const workspaceName = workspaceStore.currentWorkspace?.name
    if (!workspaceName) {
      return
    }
    await router.push({
      name: RouteName.DatabaseItemFile,
      params: {
        workspaceName,
        databaseId: databaseItemFile.databaseId,
        databaseItemId: databaseItemFile.databaseItemId,
        databaseItemFileId: databaseItemFile.id,
      },
    })
    return
  }

  if (globalResult.type === ResourceType.CloudItem) {
    const cloudItem = globalResult.typePayload as ICloudItemResponseInstance
    if (cloudItem.type === CloudItemType.Catalog) {
      await router.push({
        name: RouteName.CloudCatalog,
        params: {
          folderId: cloudItem.folderId,
          catalogCloudItemId: cloudItem.id,
        },
      })
      return
    }
    if (!cloudItem.parentCloudItemId) {
      await router.push({
        name: RouteName.Folder,
        params: {
          folderId: cloudItem.folderId,
        },
      })
      return
    }
    await router.push({
      name: RouteName.CloudCatalog,
      params: {
        folderId: cloudItem.folderId,
        catalogCloudItemId: cloudItem.parentCloudItemId,
      },
    })
  }
}

async function onSelectSuggestion(suggestion: {id: string}) {
  const selectedSuggestion = suggestionItems.value.find(item => item.key === suggestion.id)
  if (!selectedSuggestion) {
    return
  }
  try {
    await navigateToSearchResult(selectedSuggestion)
  } finally {
    appLayoutStore.closeSearchOverlay()
  }
}

async function onSubmitSearch() {
  if (!searchText.value.trim()) {
    return
  }
  const query = searchText.value.trim()
  submittedSearchText.value = query
  isLoadingSubmittedResults.value = true
  try {
    const {results} = await searchApiStore.search(query, 100)
    submittedServerResults.value = results
  } catch (e) {
    submittedServerResults.value = []
  } finally {
    isLoadingSubmittedResults.value = false
  }
  isFullSearchActive.value = true
}

function onSearchInputBlur() {
  setTimeout(() => {
    appLayoutStore.closeSearchOverlay()
  }, 0)
}
</script>

<template>
  <div class="home-search" ref="homeSearchRootRef">
    <div class="search">
      <SearchInputComponent
        ref="searchInputRef"
        v-model="searchText"
        :placeholder="t('general.search')"
        :suggestions="suggestionItems.map(item => ({id: item.key, label: item.label, description: item.description, typeLabel: item.typeLabel, snippetParts: item.snippetParts, imageUrl: item.leadingImageUrl, iconName: item.leadingIconName, descriptionIconName: item.descriptionIconName}))"
        :isLoadingSuggestions="isLoadingGlobalResults"
        :hasMoreSuggestions="hasMoreServerResults"
        :isLoadingMoreSuggestions="isLoadingMoreServerResults"
        :idleListCaption="t('general.Recent')"
        :stackReferenceElement="homeSearchRootRef"
        v-model:activeSuggestionIndex="currentSuggestionIndex"
        autoFocus
        @selectSuggestion="onSelectSuggestion"
        @submit="onSubmitSearch"
        @blur="onSearchInputBlur"
        @loadMore="onLoadMoreSuggestions"
      />
    </div>
    <button
      type="button"
      class="exit"
      :aria-label="t('general.cancel')"
      @click="appLayoutStore.closeSearchOverlay()"
    >
      <IconComponent :name="IconNameType.Xmark" :weightType="IconWeightType.Solid"/>
    </button>

    <SearchFilterListComponent
      v-if="isFullSearchActive && submittedSearchText"
      v-model="activeTypeFilter"
      :filters="availableTypeFilters"
      class="filters"
    />

    <div class="results" v-if="false">
      <template v-if="isFullSearchActive && submittedSearchText">
        <section class="section">
          <div class="section-header">
            <div class="section-title">{{t('general.Resources')}}</div>
            <div class="section-count">{{filteredFullSearchResults.length}}</div>
          </div>

          <div class="section-content" v-if="filteredFullSearchResults.length">
            <DashboardSearchItem
              v-for="item in filteredFullSearchResults"
              :key="item.key"
              :item="item.item"
              :showSpaceName="true"
              :isGlobalResult="item.isGlobalResult"
            />
          </div>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <IconComponent :name="IconNameType.Search"/>
            </div>
            <div class="empty-title">{{t('general.noResults')}}</div>
            <div class="empty-description">{{t('general.noResourcesMatch')}}</div>
          </div>
        </section>
      </template>

      <template v-else>
        <template v-if="hasResults || isLoadingSubmittedResults">
          <section class="section" v-if="localSubmittedItems.length">
            <div class="section-header">
              <div class="section-title">{{t('general.resources')}}</div>
            </div>
            <div class="section-content">
              <DashboardSearchItem
                v-for="item in localSubmittedItems"
                :key="item.id"
                :item="item"
                :showSpaceName="true"
              />
            </div>
          </section>

          <section class="section" v-if="submittedServerResults.length || isLoadingSubmittedResults">
            <div class="section-header">
              <div class="section-title">{{t('general.searchResults')}}</div>
              <LoadingComponent v-if="isLoadingSubmittedResults" :size="12"/>
            </div>
            <div class="section-content" v-if="!isLoadingSubmittedResults || submittedServerResults.length">
              <DashboardSearchItem
                v-for="item in submittedServerResults"
                :key="`${item.type}-${item.typePayload.id}`"
                :item="item"
                :showSpaceName="true"
                :isGlobalResult="true"
              />
            </div>
            <div class="skeleton-list" v-else>
              <div v-for="index in 3" :key="index" class="skeleton-item">
                <div class="skeleton-icon"/>
                <div class="skeleton-content">
                  <div class="skeleton-line is-primary"/>
                  <div class="skeleton-line is-secondary"/>
                </div>
              </div>
            </div>
          </section>
        </template>

        <div v-else-if="isFullSearchActive && submittedSearchText && !isLoadingSubmittedResults" class="empty-state">
          <div class="empty-icon">
            <IconComponent :name="IconNameType.Search"/>
          </div>
          <div class="empty-title">{{t('general.noResults')}}</div>
        </div>

        <div v-else class="empty-state is-idle">
          <div class="empty-icon">
            <IconComponent :name="IconNameType.Search"/>
          </div>
          <div class="empty-title">{{t('general.searchForItemsPlaceholder')}}</div>
          <div class="empty-description">
            {{t('general.searchStartTypingDescription')}}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.home-search {
  display: grid;
  grid-template-columns: 1fr minmax(480px, 1fr) 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    ". search exit"
    "filters results results";
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  gap: map-get(variables.$spacing, space-0-5);
  box-sizing: border-box;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, min(36rem, 100%)) minmax(0, auto);
    overflow-x: hidden;
  }
}

.search {
  grid-area: search;
  min-width: 0;
  width: 100%;
  max-width: 36rem;
  justify-self: center;
  padding: map-get(variables.$spacing, space-2);
  box-sizing: border-box;
  padding-top: map-get(variables.$spacing, space-8);
}

.exit {
  grid-area: exit;
  justify-self: end;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: map-get(variables.$sizes, size-xl);
  height: map-get(variables.$sizes, size-xl);
  padding: 0;
  margin-right: .5rem;
  box-sizing: border-box;
  border: 0;
  border-radius: map-get(variables.$radiuses, radius-full);
  background: transparent;
  color: map-get(variables.$colors, content-dim);
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: map-get(variables.$colors, container-low);
      color: map-get(variables.$colors, content-normal);
    }
  }
}

.filters {
  grid-area: filters;
  padding: map-get(variables.$spacing, space-4);
  background: transparent;
  min-width: 0;
}

.results {
  grid-area: results;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-4) map-get(variables.$spacing, space-2);
  box-sizing: border-box;
}

.close {
  display: none;
}

.eyebrow {
  font-size: map-get(variables.$textSizes, text-xs);
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: map-get(variables.$colors, content-dim);
}

.title {
  font-size: map-get(variables.$textSizes, text-3xl);
  font-weight: 600;
  line-height: 1.1;
  color: map-get(variables.$colors, content-normal);
}

.subtitle {
  color: map-get(variables.$colors, content-dim);
  max-width: 40rem;
}

.input-shell {
  display: flex;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-3);
  width: min(100%, 36rem);
  min-height: map-get(variables.$sizes, size-xl);
  padding: 0 map-get(variables.$spacing, space-4);
  border-radius: calc(variables.$borderRadius * 1.5);
  background: map-get(variables.$colors, container-lowest);
}

.input-icon {
  flex-shrink: 0;
  color: map-get(variables.$colors, content-dim);
}

.input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  padding: 0;
  background: transparent;
  color: map-get(variables.$colors, content-normal);
  font-size: map-get(variables.$textSizes, text-base);

  &::placeholder {
    color: map-get(variables.$colors, content-dim);
  }
}

.clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: map-get(variables.$sizes, size-lg);
  height: map-get(variables.$sizes, size-lg);
  padding: 0;
  border: 0;
  border-radius: map-get(variables.$radiuses, radius-full);
  background: transparent;
  color: map-get(variables.$colors, content-dim);
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: map-get(variables.$colors, container-low);
      color: map-get(variables.$colors, content-normal);
    }
  }
}

@media (max-width: 960px) {
  .filters {
    padding-left: map-get(variables.$spacing, space-2);
    padding-right: map-get(variables.$spacing, space-2);
  }

  .results {
    padding-left: map-get(variables.$spacing, space-2);
    padding-right: map-get(variables.$spacing, space-2);
  }
}

.section {
  display: grid;
  gap: map-get(variables.$spacing, space-3);
  padding: map-get(variables.$spacing, space-4);
  border-radius: calc(variables.$borderRadius * 1.25);
  background: map-get(variables.$colors, surface);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: map-get(variables.$spacing, space-3);
}

.section-title {
  font-size: map-get(variables.$textSizes, text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: map-get(variables.$colors, content-dim);
}

.section-count {
  color: map-get(variables.$colors, content-dim);
  font-size: map-get(variables.$textSizes, text-sm);
}

.section-content {
  display: grid;
}

.skeleton-list {
  display: grid;
  gap: map-get(variables.$spacing, space-3);
}

.skeleton-item {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-3);
}

.skeleton-icon {
  width: map-get(variables.$sizes, size-2xl);
  height: map-get(variables.$sizes, size-2xl);
  border-radius: calc(variables.$borderRadius * 1.25);
  background: map-get(variables.$colors, container-low);
}

.skeleton-content {
  display: grid;
  gap: map-get(variables.$spacing, space-2);
  flex: 1;
}

.skeleton-line {
  height: 0.75rem;
  border-radius: map-get(variables.$radiuses, radius-full);
  background: map-get(variables.$colors, container-low);

  &.is-primary {
    width: 35%;
  }

  &.is-secondary {
    width: 50%;
  }
}

.empty-state {
  display: grid;
  justify-items: center;
  align-content: center;
  gap: map-get(variables.$spacing, space-3);
  min-height: 18rem;
  padding: map-get(variables.$spacing, space-8);
  text-align: center;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: map-get(variables.$radiuses, radius-full);
  background: map-get(variables.$colors, container-low);
  color: map-get(variables.$colors, content-dim);
}

.empty-title {
  font-size: map-get(variables.$textSizes, text-base);
  font-weight: 600;
  color: map-get(variables.$colors, content-normal);
}

.empty-description {
  color: map-get(variables.$colors, content-dim);
  max-width: 24rem;
}
</style>
